/**
 * Sugerencia de cuánto deja el Ratón Pérez, y la notita que acompaña la moneda.
 *
 * La guía `cuanto-deja-el-raton-perez` no tenía nada que hacer: la respuesta
 * ("1 a 5 euros") está en la primera línea, así que la intención de búsqueda
 * quedaba satisfecha en diez segundos y el visitante se iba. Esto convierte la
 * cifra genérica en la del caso concreto del padre, y le deja un artefacto.
 *
 * Regla de honestidad: solo damos rango donde tenemos base real (las cifras que
 * ya afirma la guía). Donde no la hay — monedas volátiles, países que la guía
 * menciona sin dar importes — decimos que preguntes en el colegio en vez de
 * inventar un número. Un número inventado sobre cuánto dinero dar a un niño es
 * peor que no dar ninguno.
 */

import type { ComposedLetter, ToothKind } from './tooth-letter'

export type CountryKey = 'es' | 'mx' | 'ar' | 'other'

export type MoneyRange = { min: number; max: number }

export type MoneySuggestion =
  | { kind: 'range'; range: MoneyRange; formatted: string }
  /** Sin base fiable para dar una cifra. `reason` explica por qué. */
  | { kind: 'ask-around'; reason: string }

type CountryMoney = {
  label: string
  /** Rangos por tipo de diente. `null` = no tenemos base para este país. */
  ranges: (Record<ToothKind, MoneyRange> & { merit: MoneyRange }) | null
  /** Formatea un rango en la moneda local. */
  format: (range: MoneyRange) => string
  /** Motivo por el que no damos cifra (solo si `ranges` es null). */
  reason?: string
}

const ASK_AROUND_ARGENTINA =
  'En Argentina la cifra cambia demasiado rápido para que una referencia nuestra te sirva. La mejor guía, de lejos: pregunta a otras familias del colegio.'

const ASK_AROUND_OTHER =
  'No tenemos una referencia fiable para tu país, y preferimos no inventarla. Pregunta a otras familias del colegio: lo que se estila en el entorno del niño vale más que cualquier media.'

export const COUNTRIES: Record<CountryKey, CountryMoney> = {
  // Cifras de la propia guía: primer diente 3-5 €, siguientes y muelas 1-3 €,
  // diente "con mérito" 2-5 €.
  es: {
    label: 'España',
    ranges: {
      first: { min: 3, max: 5 },
      next: { min: 1, max: 3 },
      molar: { min: 1, max: 3 },
      merit: { min: 2, max: 5 },
    },
    format: (range) => `${range.min}-${range.max} €`,
  },
  // La guía solo da un rango general para México (20-50 pesos), sin desglose por
  // diente: se aplica igual a los tres en vez de inventar el desglose.
  mx: {
    label: 'México',
    ranges: {
      first: { min: 20, max: 50 },
      next: { min: 20, max: 50 },
      molar: { min: 20, max: 50 },
      merit: { min: 30, max: 50 },
    },
    format: (range) => `${range.min}-${range.max} pesos`,
  },
  ar: { label: 'Argentina', ranges: null, format: () => '', reason: ASK_AROUND_ARGENTINA },
  other: { label: 'Otro país', ranges: null, format: () => '', reason: ASK_AROUND_OTHER },
}

export const COUNTRY_ORDER: CountryKey[] = ['es', 'mx', 'ar', 'other']

/**
 * El mérito (una extracción en el dentista, un golpe, mucho miedo) no sustituye
 * al rango del diente: se combina con él tomando el techo más alto de los dos,
 * que es como lo describe la tabla de la guía.
 */
function widen(base: MoneyRange, merit: MoneyRange): MoneyRange {
  return { min: Math.max(base.min, merit.min), max: Math.max(base.max, merit.max) }
}

export function suggestToothMoney(input: {
  country: CountryKey
  tooth: ToothKind
  hasMerit: boolean
}): MoneySuggestion {
  const country = COUNTRIES[input.country]
  if (!country.ranges) {
    return { kind: 'ask-around', reason: country.reason ?? ASK_AROUND_OTHER }
  }

  const base = country.ranges[input.tooth]
  const range = input.hasMerit ? widen(base, country.ranges.merit) : base
  return { kind: 'range', range, formatted: country.format(range) }
}

/**
 * Consejo que acompaña siempre a la cifra. La coherencia importa más que el
 * importe — los niños comparan en el colegio y entre hermanos — y es lo único
 * que podemos afirmar con seguridad para cualquier país.
 */
export const COHERENCE_ADVICE =
  'Sea la cifra que sea, lo que más cuenta es mantenerla: los niños comparan en el colegio y entre hermanos. Mejor una cantidad pequeña y sostenible durante los veinte dientes que un golpe de generosidad imposible de repetir.'

/**
 * Notita corta del ratón para dejar junto a la moneda. Devuelve la misma forma
 * que la carta larga para poder reusar tal cual el render a PNG y la impresión.
 */
export function composeToothNote(input: { childName: string; amount?: string }): ComposedLetter {
  const name = input.childName.trim()
  const amount = input.amount?.trim()
  const paragraphs = ['Tu diente ya está en mi casita: brillaba tanto que lo encontré a la primera.']
  if (amount) paragraphs.push(`Te dejo ${amount} para darte las gracias.`)
  paragraphs.push('Sigue cepillándote cada día, que el próximo también lo quiero reluciente.')

  return {
    greeting: `¡Hola, ${name}!`,
    paragraphs,
    signoff: 'Con cariño,',
    signature: 'Ratón Pérez',
  }
}
